import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import { SwitchTenantDialog, type SwitchTarget } from './SwitchTenantDialog';

// ─────────────────────────────────────────────────────────────
// Session renewed on the home tenant under a client file
//
// Each tab holds its own access token, and a refresh keeps the tab's client
// file while its step-up is within its window. Past it, or once the access
// is gone, the refresh brings the token back to the home tenant while the
// screens still show the file; `apiClient` then holds every request of the
// tab. This overlay blocks the page and offers the two ways out: reopen the
// file with a new step-up, keeping the page and any unsaved entry, or open
// the home tenant in this tab, which discards them.
// ─────────────────────────────────────────────────────────────

export function TenantMismatchOverlay() {
  const { tenant, isAuthenticated, renewedTenant, adoptRenewedSession } = useAuth();
  const navigate = useNavigate();
  const [stepUpTarget, setStepUpTarget] = useState<SwitchTarget | null>(null);

  const mismatch = isAuthenticated && renewedTenant !== null && tenant !== null;
  const open = mismatch && stepUpTarget === null;
  const dialogRef = useDialogA11y<HTMLDivElement>({ open, onClose: () => {} });

  // The page behind is out of reach of the keyboard and assistive
  // technology while the overlay is up.
  useEffect(() => {
    if (!open) return;
    const root = document.getElementById('root');
    if (!root) return;
    root.setAttribute('inert', '');
    root.setAttribute('aria-hidden', 'true');
    return () => {
      root.removeAttribute('inert');
      root.removeAttribute('aria-hidden');
    };
  }, [open]);

  // The overlay is up only while the tab's token is on another tenant than
  // its screens, so reopening the file is a switch, through the step-up
  // dialog, keeping the page.
  const goBack = useCallback(() => {
    if (!tenant) return;
    setStepUpTarget({ tenantId: tenant.id, name: tenant.name });
  }, [tenant]);

  const openRenewed = useCallback(() => {
    adoptRenewedSession();
    navigate('/');
  }, [adoptRenewedSession, navigate]);

  if (!mismatch || !renewedTenant || !tenant) return null;

  if (stepUpTarget) {
    return (
      <SwitchTenantDialog target={stepUpTarget} keepPage onClose={() => setStepUpTarget(null)} />
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="tenant-mismatch-title"
        aria-describedby="tenant-mismatch-description"
        className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" aria-hidden="true" />
          <div>
            <h2 id="tenant-mismatch-title" className="text-lg font-bold text-slate-900">
              Votre session a été renouvelée sur le dossier {renewedTenant.name}.
            </h2>
            <div id="tenant-mismatch-description" className="mt-2 space-y-2 text-sm text-slate-600">
              <p>
                Cet onglet affiche toujours le dossier {tenant.name}. Aucune modification n’est
                envoyée tant que vous n’avez pas choisi.
              </p>
              <p>
                « Revenir à ce dossier » conserve cette page et votre saisie en cours, après
                confirmation de votre identité. « Recharger » ouvre le dossier {renewedTenant.name}{' '}
                dans cet onglet et abandonne la saisie en cours.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-lagune-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-lagune-800 disabled:opacity-50"
          >
            Revenir à ce dossier
          </button>
          <button
            type="button"
            onClick={openRenewed}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            Recharger
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
